import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articles, categories, faqs } from '@/mocks/articles';
import { Link } from 'react-router-dom';
import { useState } from 'react';

const featuredArticles = articles.filter((a) => a.featured);
const latestArticles = articles.slice(0, 6);
const zone1Articles = articles.filter((a) => a.zone === 'zone1').slice(0, 4);
const zone2Articles = articles.filter((a) => a.zone === 'zone2').slice(0, 4);

export default function Home() {
  const [activeTab, setActiveTab] = useState<'all' | 'zone1' | 'zone2'>('all');
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [subscribeMessage, setSubscribeMessage] = useState('');

  const filteredArticles =
    activeTab === 'all'
      ? articles
      : articles.filter((a) => a.zone === activeTab);

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setSubscribeStatus('success');
      setSubscribeMessage('订阅成功！感谢您的关注。');
      return;
    }
    setSubscribeStatus('loading');
    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qqpcme3oq4jqumtp40', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setSubscribeStatus('success');
        setSubscribeMessage('订阅成功！感谢您的关注。');
        setEmail('');
      } else {
        setSubscribeStatus('error');
        setSubscribeMessage(parsed?.meta?.message || parsed?.message || '订阅失败，请稍后再试。');
      }
    } catch {
      setSubscribeStatus('error');
      setSubscribeMessage('网络错误，请稍后再试。');
    }
  };

  return (
    <>
      <SchemaOrg type="WebSite" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '国产一区二区精选内容导航平台 - 首页',
          description: '国产一区二区汇聚海量优质国产内容资源，按分区精准分类，一区二区清晰划分，帮助用户快速找到所需内容。',
          url: 'https://www.guochanyiqu.com/',
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20cinematic%20content%20platform%2C%20warm%20cinema-like%20atmosphere%2C%20soft%20golden%20and%20amber%20lighting%2C%20abstract%20flowing%20shapes%2C%20elegant%20minimalist%20design%2C%20cinematic%20composition%2C%20dark%20rich%20background%20with%20warm%20highlights%2C%20no%20text&width=1920&height=800&seq=home-hero-main&orientation=landscape"
              alt="国产一区二区精选内容导航平台"
              title="国产一区二区精选内容导航平台"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center px-4 w-full max-w-3xl">
                <h1 className="font-heading text-3xl md:text-5xl font-black text-white mb-4 leading-tight">
                  国产一区二区精选内容导航平台
                </h1>
                <p className="text-base md:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
                  汇聚海量优质国产内容资源，按分区精准分类，一区二区清晰划分，帮助您快速找到心仪的国产内容。平台持续更新，资源丰富，分类明确，体验流畅。
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/category/zone1"
                    className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    探索一区精选
                  </Link>
                  <Link
                    to="/category/zone2"
                    className="px-8 py-3 bg-white/10 text-white border border-white/30 rounded-full font-medium text-sm whitespace-nowrap hover:bg-white/20 transition-colors cursor-pointer"
                  >
                    探索二区精选
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Category Introduction */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="group block p-6 md:p-8 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-lg shrink-0 ${
                          cat.color === 'accent'
                            ? 'bg-accent-100 text-accent-600'
                            : 'bg-secondary-100 text-secondary-600'
                        }`}
                      >
                        <i className={`${cat.icon} text-2xl md:text-3xl`}></i>
                      </div>
                      <div className="flex-1">
                        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors">
                          {cat.name}
                        </h2>
                        <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                          {cat.description}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {cat.subCategories.map((sub) => (
                            <span
                              key={sub.id}
                              className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 border border-background-200"
                            >
                              {sub.name}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="w-10 h-10 flex items-center justify-center shrink-0 text-foreground-400 group-hover:text-primary-500 transition-colors">
                        <i className="ri-arrow-right-line text-xl"></i>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Articles */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6 md:mb-8">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  精选推荐
                </h2>
                <Link
                  to="/category/zone1"
                  className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer whitespace-nowrap"
                >
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {featuredArticles.map((article, idx) => (
                  <ArticleCard
                    key={article.id}
                    article={article}
                    variant={idx === 0 ? 'featured' : 'default'}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Tab Switcher Content Area */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6 md:mb-8">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  最新内容
                </h2>
                <div className="flex items-center bg-background-100 rounded-full px-1 py-1 border border-background-200">
                  <button
                    onClick={() => setActiveTab('all')}
                    className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-all cursor-pointer ${
                      activeTab === 'all'
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-950'
                    }`}
                  >
                    全部
                  </button>
                  <button
                    onClick={() => setActiveTab('zone1')}
                    className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-all cursor-pointer ${
                      activeTab === 'zone1'
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-950'
                    }`}
                  >
                    一区精选
                  </button>
                  <button
                    onClick={() => setActiveTab('zone2')}
                    className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-all cursor-pointer ${
                      activeTab === 'zone2'
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-950'
                    }`}
                  >
                    二区精选
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>
          </section>

          {/* Two Zone Highlights */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Zone 1 */}
                <div>
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="font-heading text-xl font-bold text-foreground-950 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-primary-500"></span>
                      一区精选
                    </h3>
                    <Link
                      to="/category/zone1"
                      className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                    >
                      更多 <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                  <div className="space-y-3">
                    {zone1Articles.map((article) => (
                      <ArticleCard
                        key={article.id}
                        article={article}
                        variant="compact"
                      />
                    ))}
                  </div>
                </div>

                {/* Zone 2 */}
                <div>
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="font-heading text-xl font-bold text-foreground-950 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-accent-500"></span>
                      二区精选
                    </h3>
                    <Link
                      to="/category/zone2"
                      className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                    >
                      更多 <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                  <div className="space-y-3">
                    {zone2Articles.map((article) => (
                      <ArticleCard
                        key={article.id}
                        article={article}
                        variant="compact"
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                {[
                  { value: '1000+', label: '精选内容', icon: 'ri-file-list-3-line' },
                  { value: '50万+', label: '月活跃用户', icon: 'ri-user-line' },
                  { value: '99.7%', label: '内容满意度', icon: 'ri-heart-line' },
                  { value: '365天', label: '持续更新', icon: 'ri-refresh-line' },
                ].map((stat) => (
                  <div key={stat.label} className="p-4">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className={`${stat.icon} text-xl`}></i>
                    </div>
                    <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                      {stat.value}
                    </div>
                    <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="py-10 md:py-14 bg-background-100">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-8">
                常见问题
              </h2>
              <SchemaOrg type="FAQPage" faqData={faqs} />
              <div className="space-y-3">
                {faqs.map((faq, idx) => (
                  <details
                    key={idx}
                    className="group bg-background-50 rounded-lg border border-background-200 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                      <h4 className="font-heading text-sm md:text-base font-semibold text-foreground-950 pr-4">
                        {faq.question}
                      </h4>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform shrink-0"></i>
                    </summary>
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Newsletter */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                订阅我们的资讯
              </h2>
              <p className="text-sm text-foreground-600 mb-6">
                第一时间获取国产优质内容的最新推荐与精彩解读
              </p>
              <form onSubmit={handleSubscribe} data-readdy-form="true" className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <div className="honeypot-wrap">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="请输入您的邮箱地址"
                  required
                  className="flex-1 px-4 py-3 text-sm rounded-full border border-background-300 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
                <button
                  type="submit"
                  disabled={subscribeStatus === 'loading'}
                  className="px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer"
                >
                  {subscribeStatus === 'loading' ? '提交中...' : '立即订阅'}
                </button>
              </form>
              {subscribeStatus !== 'idle' && (
                <p
                  className={`mt-4 text-sm ${
                    subscribeStatus === 'success' ? 'text-accent-600' : 'text-primary-600'
                  }`}
                >
                  {subscribeMessage}
                </p>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}